<template>
  <div class="login-panel-body__content">
    <!-- Tab 切换 -->
    <div class="login-tabs">
      <div class="login-tabs__header">
        <div
          v-for="tab in tabs"
          :key="tab.name"
          :class="['login-tabs__item', { 'is-active': activeTab === tab.name }]"
          @click="activeTab = tab.name"
        >
          {{ tab.label }}
        </div>
      </div>
      <div class="login-tabs__active-bar" :style="{ transform: `translateX(${activeTabIndex * 100}%)`, width: `${100 / tabs.length}%` }"></div>
    </div>

    <!-- Tab 内容 -->
    <div class="login-tabs__content">
      <!-- 账号登录 -->
      <div v-show="activeTab === 'password'" class="login-panel__tab-password">
        <form class="login-form" @submit.prevent="loginSubmit">
          <div class="form-item">
            <input
              v-model="formPassword.email"
              type="text"
              placeholder="邮箱/手机号"
              class="form-input"
              maxlength="128"
            />
          </div>

          <div class="form-item">
            <div class="password-input-wrapper">
              <input
                v-model="formPassword.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="密码"
                class="form-input"
              />
              <button
                type="button"
                class="password-toggle"
                @click="showPassword = !showPassword"
                tabindex="-1"
              >
                <PtIcon
                  :icon="showPassword ? 'SolidSee' : 'SolidNotSee'"
                  class="icon-eye"
                />
              </button>
            </div>
          </div>

          <div v-if="errorMessage" class="form-item error-message">
            {{ errorMessage }}
          </div>

          <div class="form-item">
            <button
              type="submit"
              class="btn-primary btn-block"
              :disabled="isLoading || !formPassword.email || !formPassword.password"
            >
              {{ isLoading ? '登录中...' : '登录' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Token 登录 -->
      <div v-show="activeTab === 'token'" class="login-panel__tab-password">
        <div class="token-hint">
          <p>1. 先在 <a :href="qaUrl" target="_blank" class="link-primary">QA 环境</a> 登录</p>
          <p>2. 打开开发者工具 (F12) → Application → Local Storage</p>
          <p>3. 复制 <code>access_token</code> 的值粘贴到下方</p>
        </div>
        <form class="login-form" @submit.prevent="tokenLoginSubmit">
          <div class="form-item">
            <textarea
              v-model="tokenInput"
              placeholder="粘贴 access_token..."
              class="form-input token-textarea"
              rows="3"
            />
          </div>

          <div v-if="tokenErrorMessage" class="form-item error-message">
            {{ tokenErrorMessage }}
          </div>

          <div class="form-item">
            <button
              type="submit"
              class="btn-primary btn-block"
              :disabled="isLoading || !tokenInput"
            >
              {{ isLoading ? '登录中...' : '使用 Token 登录' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import PtIcon from '@patsnap-biz/icon'
import '@patsnap-ui/icon/assets/solid/NotSee.svg'
import '@patsnap-ui/icon/assets/solid/see.svg'
import axios from 'axios'

const emit = defineEmits<{
  success: [data: { token: string; user: any }]
}>()

const qaUrl = 'https://qa-eureka.zhihuiya.com'

const tabs = [
  { label: '账号登录', name: 'password' },
  { label: 'Token 登录', name: 'token' },
]

const activeTab = ref('password')
const showPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const tokenErrorMessage = ref('')
const tokenInput = ref('')

const formPassword = ref({
  email: '',
  password: '',
})

const activeTabIndex = computed(() => {
  return tabs.findIndex(tab => tab.name === activeTab.value)
})

// 真实登录 API
const loginSubmit = async () => {
  if (!formPassword.value.email || !formPassword.value.password) {
    return
  }

  isLoading.value = true
  errorMessage.value = ''

  try {
    // 调用 passport 登录 API
    const response = await axios.post('/proxy-passport/oauth/token', {
      grant_type: 'password',
      username: formPassword.value.email,
      password: formPassword.value.password,
      client_id: '4355e565135c41b3b7395cbaf94f1b67',
      client_name: 'eureka',
    }, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      transformRequest: [(data) => {
        const params = new URLSearchParams()
        for (const key in data) {
          params.append(key, data[key])
        }
        return params.toString()
      }],
    })

    if (response.data?.access_token) {
      // 保存 token 到 localStorage（使用与 QA 环境相同的 key）
      localStorage.setItem('authToken', response.data.access_token)
      localStorage.setItem('access_token', response.data.access_token)
      if (response.data.refresh_token) {
        localStorage.setItem('refresh_token', response.data.refresh_token)
      }

      console.log('登录成功')
      emit('success', {
        token: response.data.access_token,
        user: { email: formPassword.value.email }
      })
    } else {
      errorMessage.value = '登录失败，请检查账号密码'
    }
  } catch (error: any) {
    console.error('登录失败:', error)
    errorMessage.value = error.response?.data?.error_description || error.message || '登录失败'
  } finally {
    isLoading.value = false
  }
}

// Token 登录
const tokenLoginSubmit = () => {
  if (!tokenInput.value.trim()) {
    return
  }

  isLoading.value = true
  tokenErrorMessage.value = ''

  try {
    const token = tokenInput.value.trim()

    // 保存 token 到 localStorage（使用与 QA 环境相同的 key）
    localStorage.setItem('authToken', token)
    localStorage.setItem('access_token', token)

    console.log('Token 登录成功')
    emit('success', {
      token: token,
      user: { email: 'token-login' }
    })
  } catch (error: any) {
    console.error('Token 登录失败:', error)
    tokenErrorMessage.value = error.message || 'Token 登录失败'
  } finally {
    isLoading.value = false
  }
}
</script>

<style lang="scss" scoped>
.login-panel-body__content {
  width: 100%;
}

/* Tab 样式 */
.login-tabs {
  position: relative;
  border-bottom: 1px solid #e4e7ed;
  margin-bottom: 24px;

  &__header {
    display: flex;
    justify-content: center;
    gap: 40px;
  }

  &__item {
    padding: 12px 4px;
    font-size: 16px;
    color: #8993a4;
    cursor: pointer;
    position: relative;
    transition: color 0.3s;

    &:hover {
      color: #606266;
    }

    &.is-active {
      color: #020a1a;
      font-weight: bold;
    }
  }

  &__active-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 2px;
    background-color: #020a1a;
    transition: transform 0.3s;
  }
}

.login-tabs__content {
  min-height: 200px;
}

/* 表单样式 */
.login-form {
  .form-item {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
      margin-top: 24px;
    }
  }
}

.form-input {
  width: 100%;
  height: 40px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #409eff;
  }

  &::placeholder {
    color: #a8abb2;
  }
}

.token-textarea {
  height: auto;
  padding: 10px 15px;
  resize: vertical;
  font-family: monospace;
  font-size: 12px;
}

.password-input-wrapper {
  position: relative;

  .password-toggle {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    opacity: 0.5;
    transition: opacity 0.3s;

    &:hover {
      opacity: 0.8;
    }

    .icon-eye {
      width: 18px;
      height: 18px;
      fill: #909399;
    }
  }
}

.btn-primary {
  height: 40px;
  padding: 0 20px;
  background-color: #409eff;
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover:not(:disabled) {
    background-color: #66b1ff;
  }

  &:active:not(:disabled) {
    background-color: #3a8ee6;
  }

  &:disabled {
    background-color: #a0cfff;
    cursor: not-allowed;
  }
}

.btn-block {
  width: 100%;
}

.link-primary {
  color: #409eff;
  text-decoration: none;

  &:hover {
    color: #66b1ff;
    text-decoration: underline;
  }
}

.error-message {
  color: #f56c6c;
  font-size: 13px;
  margin-top: -8px !important;
}

.token-hint {
  background: #f5f7fa;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #606266;
  line-height: 1.8;

  p {
    margin: 0;
  }

  code {
    background: #e4e7ed;
    padding: 2px 6px;
    border-radius: 3px;
    font-family: monospace;
    color: #409eff;
  }
}
</style>
